/**
 * Pure functions for computing per-month allocation % and animal counts
 * for each stock class type, based on Teagasc / AHDB demand tables.
 */

export type StockClassTypeId =
  | "DAIRY_COW"
  | "REPLACEMENT_HEIFER"
  | "BREEDING_COW"
  | "GROWING_CATTLE"
  | "BREEDING_EWE"
  | "GROWING_LAMB"
  | "BULL"
  | "RAM";

export interface StockClassTypeConfig {
  id: StockClassTypeId;
  label: string;
  description: string;
  defaultWeightKg: number;
  spawnsClass?: string;
}

export const STOCK_CLASS_TYPES: StockClassTypeConfig[] = [
  {
    id: "DAIRY_COW",
    label: "Dairy Cows",
    description: "Milking cows",
    defaultWeightKg: 550,
  },
  {
    id: "REPLACEMENT_HEIFER",
    label: "Replacement Heifers",
    description: "Growing heifers for dairy herd replacement",
    defaultWeightKg: 350,
  },
  {
    id: "BREEDING_COW",
    label: "Breeding Cows / Heifers",
    description: "Suckler cows — includes calf up to 200 days",
    defaultWeightKg: 500,
    spawnsClass: "Growing Cattle",
  },
  {
    id: "GROWING_CATTLE",
    label: "Growing Cattle / Stores",
    description: "Store or finishing cattle",
    defaultWeightKg: 350,
  },
  {
    id: "BREEDING_EWE",
    label: "Breeding Ewes / Gimmers",
    description: "Breeding ewes — includes lambs up to 100 days",
    defaultWeightKg: 65,
    spawnsClass: "Growing Lambs",
  },
  {
    id: "GROWING_LAMB",
    label: "Growing Lambs / Stores",
    description: "Weaned lambs for finishing",
    defaultWeightKg: 35,
  },
  {
    id: "BULL",
    label: "Bulls",
    description: "Breeding bulls",
    defaultWeightKg: 800,
  },
  {
    id: "RAM",
    label: "Rams",
    description: "Breeding rams",
    defaultWeightKg: 85,
  },
];

function monthOf(date: Date): number {
  return date.getMonth();
}

/**
 * Growing cattle / replacement heifers: target DLWG (kg/day) → % body weight
 * Source: AHDB Growing Cattle demand table
 */
export function cattleDlwgToAllocation(dlwgKgDay: number): number {
  if (dlwgKgDay <= 0) return 1.5;
  if (dlwgKgDay <= 0.5) return 2.0;
  if (dlwgKgDay <= 1.0) return 2.5;
  return 3.0;
}

/**
 * Growing lambs: target DLWG (g/day) → % body weight
 * Source: AHDB Weaned Lambs demand table
 */
export function lambDlwgToAllocation(dlwgGDay: number): number {
  if (dlwgGDay <= 0) return 1.5;
  if (dlwgGDay <= 50) return 2.0;
  if (dlwgGDay <= 150) return 2.5;
  return 3.0;
}

/**
 * Dairy cows: per-month allocation % based on calving/dry-off dates and yield.
 * Formula: demand_kgDM/day = weightKg × 0.02 + yieldLDay × 0.45
 * During dry period: demand = weightKg × 0.02 (no milk production)
 */
export function computeDairyCowAllocations(
  calvingDate: Date,
  dryOffDate: Date,
  yieldLDay: number,
  weightKg: number
): number[] {
  const calvingMonth = monthOf(calvingDate);
  const dryOffMonth = monthOf(dryOffDate);

  const dryPct = 2.0;
  const lactationPct = ((weightKg * 0.02 + yieldLDay * 0.45) / weightKg) * 100;

  return Array.from({ length: 12 }, (_, m) => {
    // Lactation = calvingMonth up to (not including) dryOffMonth
    let isLactation: boolean;
    if (calvingMonth < dryOffMonth) {
      isLactation = m >= calvingMonth && m < dryOffMonth;
    } else if (calvingMonth > dryOffMonth) {
      // wraps year: e.g. calving Oct, dry-off Mar → Oct..Dec, Jan..Feb = lactation
      isLactation = m >= calvingMonth || m < dryOffMonth;
    } else {
      // same month edge case — treat whole year as lactation
      isLactation = true;
    }
    return parseFloat((isLactation ? lactationPct : dryPct).toFixed(2));
  });
}

/**
 * Breeding / suckler cows: per-month allocation % based on calving and weaning dates.
 * Source: Teagasc Suckler Cow demand table
 *   Dry maintenance:          1.5% BW
 *   Pre-calving (30 days):    2.0% BW
 *   0–2 months post-calving:  2.5% BW
 *   3–4 months post-calving:  3.0% BW
 *   >4 months to weaning:     3.5% BW
 *   Post-weaning:             1.5% BW
 */
export function computeBreedingCowAllocations(
  calvingDate: Date,
  weaningDate: Date
): number[] {
  const calvingMonth = monthOf(calvingDate);
  const weaningMonth = monthOf(weaningDate);
  const preCalvingMonth = (calvingMonth + 11) % 12;

  // Total months from calving to weaning
  const monthsToWeaning =
    weaningMonth >= calvingMonth
      ? weaningMonth - calvingMonth
      : weaningMonth + 12 - calvingMonth;

  return Array.from({ length: 12 }, (_, m) => {
    if (m === preCalvingMonth && m !== calvingMonth) return 2.0;

    const monthsPostCalving =
      m >= calvingMonth ? m - calvingMonth : m + 12 - calvingMonth;

    if (monthsPostCalving >= monthsToWeaning) return 1.5; // post-weaning
    if (monthsPostCalving <= 2) return 2.5;
    if (monthsPostCalving <= 4) return 3.0;
    return 3.5;
  });
}

/**
 * Breeding ewes: per-month allocation % based on tupping, weaning dates and scanning %.
 * Lambing date = tupping + 147 days.
 * Allocation is a weighted average of single and twin ewe values by scanning %.
 *
 * Source: Teagasc Ewe demand table
 *   Dry maintenance (pre 40 days pre-lambing): 1.5%
 *   ~40 days pre-lambing:   singles 2.5%, twins 2.5%
 *   0–1 month post-lambing: singles 2.5%, twins 3.5%
 *   >1 month to weaning:    singles 3.0%, twins 4.0%
 *   Post-weaning:           1.5%
 *
 * scanningPct: lambs per 100 ewes (e.g. 160 = 1.6 lambs/ewe)
 * twinFraction = max(0, min(1, scanningPct/100 - 1))
 */
export function computeBreedingEweAllocations(
  tuppingDate: Date,
  weaningDate: Date,
  scanningPct: number
): number[] {
  const lambingDate = new Date(
    tuppingDate.getTime() + 147 * 24 * 60 * 60 * 1000
  );
  const lambingMonth = monthOf(lambingDate);
  const weaningMonth = monthOf(weaningDate);
  const preNatalMonth = (lambingMonth + 11) % 12;

  const twinFraction = Math.max(0, Math.min(1, scanningPct / 100 - 1));
  const singleFraction = 1 - twinFraction;

  const weighted = (s: number, t: number) =>
    parseFloat((singleFraction * s + twinFraction * t).toFixed(2));

  const monthsToWeaning =
    weaningMonth >= lambingMonth
      ? weaningMonth - lambingMonth
      : weaningMonth + 12 - lambingMonth;

  return Array.from({ length: 12 }, (_, m) => {
    if (m === preNatalMonth && m !== lambingMonth) return weighted(2.5, 2.5); // 40 days pre-lambing

    const monthsPostLambing =
      m >= lambingMonth ? m - lambingMonth : m + 12 - lambingMonth;

    if (monthsPostLambing >= monthsToWeaning) return 1.5; // post-weaning
    if (monthsPostLambing === 0) return weighted(2.5, 3.5); // 0–1 month
    return weighted(3.0, 4.0); // >1 month to weaning
  });
}

/**
 * For growing classes (cattle/lambs): zero out animal count after the sale month.
 * Pass planStartDate for cross-year plans so month indices map to the correct year.
 */
export function computeAnimalCountsByMonth(
  animalCount: number,
  saleDate: Date | null,
  planStartDate?: Date | null
): number[] {
  if (!saleDate) return Array(12).fill(animalCount);
  if (!planStartDate) {
    const saleMonth = monthOf(saleDate);
    return Array.from({ length: 12 }, (_, m) =>
      m > saleMonth ? 0 : animalCount
    );
  }
  const planStartYear = planStartDate.getFullYear();
  const planStartMonth = planStartDate.getMonth();
  const saleYear = saleDate.getFullYear();
  const saleMon = saleDate.getMonth();
  return Array.from({ length: 12 }, (_, m) => {
    const yr = m >= planStartMonth ? planStartYear : planStartYear + 1;
    if (yr < saleYear) return animalCount;
    if (yr > saleYear) return 0;
    return m <= saleMon ? animalCount : 0;
  });
}

/**
 * For auto-spawned growing classes: 0 animals before weaning month, full count after.
 * Pass planStartDate for cross-year plans so month indices map to the correct year.
 *
 * Returns 12 values when weaning falls within the plan's first year, or 24 values
 * when weaning falls in the plan's second calendar year (entries 12-23 = year 1).
 * Callers should create StockClassMonthEntry objects with yearOffset:1 for indices 12-23.
 */
export function computeSpawnedAnimalCounts(
  animalCount: number,
  weaningDate: Date,
  planStartDate?: Date | null
): number[] {
  if (!planStartDate) {
    const weaningMonth = monthOf(weaningDate);
    return Array.from({ length: 12 }, (_, m) =>
      m >= weaningMonth ? animalCount : 0
    );
  }
  const planStartYear = planStartDate.getFullYear();
  const planStartMonth = planStartDate.getMonth();
  const weaningYear = weaningDate.getFullYear();
  const weaningMon = weaningDate.getMonth();

  const year0 = Array.from({ length: 12 }, (_, m) => {
    const yr = m >= planStartMonth ? planStartYear : planStartYear + 1;
    if (yr < weaningYear) return 0;
    if (yr > weaningYear) return animalCount;
    return m >= weaningMon ? animalCount : 0;
  });

  // When weaning is in the plan's second calendar year, append 12 year-1 entries
  if (weaningYear > planStartYear) {
    const year1 = Array.from({ length: 12 }, (_, m) => {
      const yr = m >= planStartMonth ? planStartYear + 1 : planStartYear + 2;
      if (yr < weaningYear) return 0;
      if (yr > weaningYear) return animalCount;
      return m >= weaningMon ? animalCount : 0;
    });
    return [...year0, ...year1];
  }

  return year0;
}
